import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { CustomDatePicker } from "../../common/FormInputs";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import useSettings from "../../../hooks/useSettings";
import BookingService from "../../../services/BookingService";
import { getErrorMessage } from "../../../helpers/functions";
import { useBookingHolidays, renderHolidayDay } from "../utils/holidays";
import { Modal } from "@material-ui/core";
import { Header, HeaderText, Title, Subtitle, Body } from "../components/Modal";
import { CloseBtn } from "../../pages/AssignCaseLifeCycleBulkCase";
import { media } from "../theme/breakpoints";
import { useSnackbar } from "notistack";
import {
  C,
  SEMI,
  REG,
  Section,
  SegLabel,
  FieldBlock,
  FloatLabel,
  RequiredStar,
} from "../WalkinBooking/Styles";

const RescheduleModal = ({ open, onClose, booking, onSubmit }) => {
  const { setting } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [hearingDate, setHearingDate] = useState("");
  // idle | checking | available | unavailable
  const [availability, setAvailability] = useState({
    status: "idle",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const holidays = useBookingHolidays();
  const holidayName = holidays[hearingDate];

  // Reschedule keeps the booked room, start time and duration —
  // only the date can be changed.
  const bookingReference = booking?.booking_reference || booking?.ref;
  const roomName = booking?.hearing_room;
  const startTime = booking?.start_time;
  const sessionDuration = booking?.session_duration;
  const currentDate = booking?.hearing_date
    ? moment(booking.hearing_date).format("YYYY-MM-DD")
    : "";

  const roomInfo = (setting?.roomsPricingInfo || []).find(
    (r) => r.roomName === roomName,
  );
  const roomLabel = roomInfo?.displayName || roomName;
  const timeLabel = `${moment(startTime, "HH:mm:ss").format("hh:mm A")} – ${moment(
    booking?.end_time,
    "HH:mm:ss",
  ).format("hh:mm A")}`;
  const durationLabel =
    sessionDuration === "halfday" ? "Half Day (3 hrs)" : "Full Day (6 hrs)";

  // If the booked start time has already passed today, today is not an option.
  const minDate = moment().isSameOrAfter(moment(startTime, "HH:mm:ss"))
    ? moment().add(1, "day")
    : moment();
  const maxDate = moment().add(3, "months");

  useEffect(() => {
    if (!hearingDate) return;
    let ignore = false;
    setAvailability({ status: "checking", message: "" });

    const query = `?hearing_date=${hearingDate}&start_time=${startTime}&booking_reference=${encodeURIComponent(
      bookingReference,
    )}`;

    BookingService.fetchAvailableRooms(query)
      .then((res) => {
        if (ignore) return;
        if (res?.available?.includes(roomName)) {
          setAvailability({ status: "available", message: "" });
        } else {
          setAvailability({
            status: "unavailable",
            message:
              res?.message ||
              `${roomLabel} is already booked for ${timeLabel} on this date. Please choose another date.`,
          });
        }
      })
      .catch((err) => {
        if (!ignore)
          setAvailability({
            status: "unavailable",
            message: getErrorMessage(err || {}),
          });
      });

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hearingDate]);

  const handleDateChange = (momentVal) => {
    setAvailability({ status: "idle", message: "" });
    setHearingDate(
      momentVal && momentVal.isValid() ? momentVal.format("YYYY-MM-DD") : "",
    );
  };

  const handleConfirm = async () => {
    if (!hearingDate || availability.status !== "available") return;
    setSubmitting(true);
    try {
      await onSubmit({
        ...booking,
        booking_reference: bookingReference || "",
        hearing_date: hearingDate,
        start_time: startTime,
        session_duration: sessionDuration,
        hearing_room: { roomName },
      });
      onClose();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Dialog $width={600}>
        <Header>
          <HeaderText>
            <Title>Reschedule Booking</Title>
            <Subtitle>{`Ref ${booking?.ref} • ${booking?.room?.name || booking?.roomName || ""}`}</Subtitle>
          </HeaderText>

          <CloseBtn onClick={onClose}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseBtn>
        </Header>
        <ModalBody>
          <Body className="scrollbar">
            <Section $delay="0s">
              <SegLabel>Current Booking</SegLabel>
              <InfoGrid>
                <div>
                  <InfoLabel>Room</InfoLabel>
                  <InfoValue>{roomLabel}</InfoValue>
                </div>
                <div>
                  <InfoLabel>Date</InfoLabel>
                  <InfoValue>
                    {currentDate
                      ? moment(currentDate, "YYYY-MM-DD").format("DD MMM YYYY")
                      : "—"}
                  </InfoValue>
                </div>
                <div>
                  <InfoLabel>Time</InfoLabel>
                  <InfoValue>{timeLabel}</InfoValue>
                </div>
                <div>
                  <InfoLabel>Duration</InfoLabel>
                  <InfoValue>{durationLabel}</InfoValue>
                </div>
              </InfoGrid>
            </Section>

            <Section $delay="0.05s">
              <SegLabel>New Date</SegLabel>
              <FieldBlock>
                <FloatLabel htmlFor="hearing_date">
                  Hearing Date <RequiredStar>*</RequiredStar>
                </FloatLabel>
                <CustomDatePicker
                  value={
                    hearingDate ? moment(hearingDate, "YYYY-MM-DD") : null
                  }
                  onChange={handleDateChange}
                  format="DD/MM/YYYY"
                  minDate={minDate}
                  maxDate={maxDate}
                  shouldDisableDate={(day) =>
                    day.format("YYYY-MM-DD") === currentDate
                  }
                  renderDay={renderHolidayDay(holidays)}
                  clearable
                  inputVariant="outlined"
                  className="input-white"
                  style={{ marginTop: 6 }}
                  emptyLabel="DD/MM/YYYY"
                />
              </FieldBlock>
              <Hint>
                Room, start time and duration stay the same — only the date
                changes.
              </Hint>
              {holidayName && (
                <Hint>
                  Govt. holiday ({holidayName}) — weekend pricing applies.
                </Hint>
              )}

              {availability.status === "checking" && (
                <StatusBanner>Checking availability...</StatusBanner>
              )}
              {availability.status === "available" && (
                <StatusBanner $ok>
                  <strong>{roomLabel}</strong> is available on{" "}
                  {moment(hearingDate, "YYYY-MM-DD").format("DD MMM YYYY")},{" "}
                  {timeLabel}.
                </StatusBanner>
              )}
              {availability.status === "unavailable" && (
                <StatusBanner $error>{availability.message}</StatusBanner>
              )}
            </Section>
          </Body>
          <ActionBar>
            <PrimaryOutlinedCloseCTAButton
              type="button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </PrimaryOutlinedCloseCTAButton>

            <PrimaryCTAButton
              type="button"
              disabled={submitting || availability.status !== "available"}
              onClick={handleConfirm}
            >
              {submitting ? "Rescheduling..." : "Confirm Reschedule"}
            </PrimaryCTAButton>
          </ActionBar>
        </ModalBody>
      </Dialog>
    </Modal>
  );
};

export default RescheduleModal;

export const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  overflow: auto;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 2px;
    height: 2px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

export const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid ${COLORS.BORDER_GREY};
  margin-top: 20px;
  background: white;
  border-radius: 0 0 12px 12px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: ${C.surface};
  border: 1px solid ${C.line};
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InfoLabel = styled.div`
  font-family: ${SEMI};
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.mutedMid};
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-family: ${SEMI};
  font-size: 14px;
  color: ${C.navy};
`;

const Hint = styled.div`
  margin-top: 8px;
  font-family: ${REG};
  font-size: 12px;
  color: ${C.muted};
`;

const StatusBanner = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  font-family: ${REG};
  font-size: 12.5px;
  line-height: 1.5;
  background: ${(p) =>
    p.$ok
      ? "rgba(0,131,140,0.07)"
      : p.$error
        ? "rgba(220,53,69,0.07)"
        : C.surface};
  border: 1px solid
    ${(p) =>
      p.$ok
        ? "rgba(0,131,140,0.18)"
        : p.$error
          ? "rgba(220,53,69,0.28)"
          : C.line};
  color: ${(p) => (p.$ok ? C.teal : p.$error ? "#b91c2c" : C.mutedMid)};

  strong {
    font-family: ${SEMI};
  }
`;

export const Dialog = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${COLORS.PRIMARY_WHITE};
  width: 100%;
  max-width: ${({ $width }) => $width}px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease-out;
  border-radius: 12px 12px 0 0;

  ${media.tablet} {
    max-height: 90vh;
    border-radius: 12px;
  }
`;
